import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, FlatList, Keyboard, KeyboardEvent } from 'react-native';
import { Accordion, QuickAddTask, TaskRow } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ITaskItemAttributes } from '@/types/admin/tasks';

interface SectionConfig {
  key: string;
  label: string;
  color?: string;
  icon?: React.ReactNode;
  quickAdd?: React.ReactNode;
}

interface TasksAccordionListProps {
  sections: SectionConfig[];
  groupedTasks: Record<string, ITaskItemAttributes[]>;
  forceOpenIncrement: number;
  hasActiveFilters: boolean;
  isArchived: boolean;
  onShowDetails: (id: number) => void;
  onEdit: (id: number) => void;
  onDuplicate?: (id: number) => void;
  onArchive?: (id: number) => void;
}

export const TasksAccordionList: React.FC<TasksAccordionListProps> = ({
  sections,
  groupedTasks,
  forceOpenIncrement,
  hasActiveFilters,
  isArchived,
  onShowDetails,
  onEdit,
  onDuplicate,
  onArchive,
}) => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const keyboardWillShow = (e: KeyboardEvent) => {
      setKeyboardHeight(e.endCoordinates.height);
    };

    const keyboardWillHide = () => {
      setKeyboardHeight(0);
    };

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showListener = Keyboard.addListener(showEvent, keyboardWillShow);
    const hideListener = Keyboard.addListener(hideEvent, keyboardWillHide);

    return () => {
      showListener.remove();
      hideListener.remove();
    };
  }, []);

  return (
    <FlatList
      style={{ flex: 1 }}
      contentContainerStyle={{ paddingBottom: keyboardHeight + 20 }}
      data={sections}
      keyExtractor={(item) => item.key}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
      renderItem={({ item }) => {
        const list = groupedTasks[item.key] || [];
        const count = list.length || 0;
        const forceOpen = count > 0 ? forceOpenIncrement : undefined;
        const headerIcon = item.icon || (
          <View style={[styles.statusDot, { backgroundColor: item.color || ADMIN_COLORS.TEXT_SECONDARY }]} />
        );
        return (
          <Accordion
            forceOpen={forceOpen}
            openWhen={hasActiveFilters && count > 0}
            closeWhen={!hasActiveFilters || (hasActiveFilters && count === 0)}
            header={(
              <View style={styles.header}>
                <View style={styles.left}> 
                  {headerIcon}
                  <Text style={styles.title}>{item.label}</Text>
                  <Text style={styles.count}>{count}</Text>
                </View>
              </View>
            )}
          >
            {list.map((t) => (
              <TaskRow
                key={`${item.key}-${t.id}`}
                task={t}
                onShowDetails={onShowDetails}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onArchive={onArchive}
                isArchived={isArchived}
              />
            ))}
            {count === 0 && item.key === 'incoming' && (
              <View style={styles.emptySection}>
                <Text style={styles.emptyText}>Incoming requests from residents will appear here</Text>
              </View>
            )}
            {item.quickAdd}
          </Accordion>
        );
      }}
    />
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  left: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statusDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '300',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  count: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  emptySection: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
  },
});


